import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  LinkIcon,
  CalendarIcon,
  TagIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

// Normalize image URLs
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');
  
  // If Cloudinary URL, return as-is
  if (normalized.includes('cloudinary.com')) {
    return normalized;
  }
  
  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }
  
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

interface LastMinuteDeal {
  _id: string;
  title: string;
  description?: string;
  image: string;
  link: string;
  linkType: 'internal' | 'external';
  package?: {
    _id: string;
    name: string;
    slug: string;
  };
  sortOrder: number;
  isActive: boolean;
  validFrom?: string;
  validTo?: string;
  discountBadge?: string;
  createdAt: string;
  updatedAt: string;
}

interface Package {
  _id: string;
  name: string;
  slug: string;
  priceUSD: number;
  priceAUD: number;
}

const AdminLastMinuteDeals: React.FC = () => {
  const { token } = useAuth();
  const [deals, setDeals] = useState<LastMinuteDeal[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState<LastMinuteDeal | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    linkType: 'internal' as 'internal' | 'external',
    packageId: '',
    sortOrder: '0',
    isActive: true,
    validFrom: '',
    validTo: '',
    discountBadge: ''
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch deals and packages
  useEffect(() => {
    fetchDeals();
    fetchPackages();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get('/api/last-minute-deals/admin', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data.deals || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/api/packages', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const openCreateForm = () => {
    setEditingDeal(null);
    setFormData({
      title: '',
      description: '',
      link: '',
      linkType: 'internal',
      packageId: '',
      sortOrder: '0',
      isActive: true,
      validFrom: '',
      validTo: '',
      discountBadge: ''
    });
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
  };

  const openEditForm = (deal: LastMinuteDeal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title,
      description: deal.description || '',
      link: deal.link,
      linkType: deal.linkType,
      packageId: deal.package?._id || '',
      sortOrder: deal.sortOrder.toString(),
      isActive: deal.isActive,
      validFrom: deal.validFrom ? new Date(deal.validFrom).toISOString().split('T')[0] : '',
      validTo: deal.validTo ? new Date(deal.validTo).toISOString().split('T')[0] : '',
      discountBadge: deal.discountBadge || ''
    });
    setImageFile(null);
    setImagePreview(resolveImageUrl(deal.image));
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('link', formData.link);
      submitData.append('linkType', formData.linkType);
      if (formData.packageId) submitData.append('package', formData.packageId);
      submitData.append('sortOrder', formData.sortOrder);
      submitData.append('isActive', formData.isActive.toString());
      if (formData.validFrom) submitData.append('validFrom', formData.validFrom);
      if (formData.validTo) submitData.append('validTo', formData.validTo);
      if (formData.discountBadge) submitData.append('discountBadge', formData.discountBadge);
      
      if (imageFile) {
        submitData.append('image', imageFile);
      } else if (editingDeal && !imageFile) {
        submitData.append('image', editingDeal.image);
      }

      const url = editingDeal 
        ? `/api/last-minute-deals/${editingDeal._id}`
        : '/api/last-minute-deals';
      
      const method = editingDeal ? 'put' : 'post';

      await axios[method](url, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowForm(false);
      fetchDeals();
    } catch (error: any) {
      console.error('Error saving deal:', error);
      alert(error.response?.data?.message || 'Failed to save deal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this deal?')) return;

    try {
      await axios.delete(`/api/last-minute-deals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDeals();
    } catch (error) {
      console.error('Error deleting deal:', error);
      alert('Failed to delete deal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Last Minute Deals - Admin | Bali Getaway</title>
      </Helmet>

      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Last Minute Deals</h1>
            <p className="text-gray-600 mt-2">Manage promotional deals and offers</p>
          </div>
          <button
            onClick={openCreateForm}
            className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all shadow-lg"
          >
            <PlusIcon className="w-5 h-5" />
            Add New Deal
          </button>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {deals.map((deal) => (
            <div key={deal._id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              {/* Deal Image */}
              <div className="relative h-48">
                <img
                  src={resolveImageUrl(deal.image)}
                  alt={deal.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  {deal.discountBadge && (
                    <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                      {deal.discountBadge}
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    deal.isActive ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                  }`}>
                    {deal.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Deal Info */}
              <div className="p-4">
                <h3 className="font-bold text-lg text-gray-900 mb-2">{deal.title}</h3>
                {deal.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{deal.description}</p>
                )}
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {deal.linkType === 'external' ? 'External Link' : 'Internal Link'}
                    </span>
                  </div>
                  
                  {deal.package && (
                    <div className="flex items-center gap-2">
                      <TagIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{deal.package.name}</span>
                    </div>
                  )}
                  
                  {(deal.validFrom || deal.validTo) && (
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {deal.validFrom && new Date(deal.validFrom).toLocaleDateString()} - {' '}
                        {deal.validTo && new Date(deal.validTo).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <button
                    onClick={() => openEditForm(deal)}
                    className="flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(deal._id)}
                    className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {deals.length === 0 && (
          <div className="text-center py-12">
            <TagIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No deals yet</h3>
            <p className="text-gray-600 mb-6">Create your first last minute deal to get started.</p>
            <button
              onClick={openCreateForm}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all"
            >
              Add First Deal
            </button>
          </div>
        )}

        {/* Deal Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-orange-600 text-white p-6 rounded-t-2xl">
                <h2 className="text-2xl font-bold">
                  {editingDeal ? 'Edit Deal' : 'Create New Deal'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Amazing Bali Deal - 50% Off!"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Describe this amazing deal..."
                  />
                </div>

                {/* Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deal Image <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview('');
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">Click to upload deal image</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Link Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link Type
                    </label>
                    <select
                      name="linkType"
                      value={formData.linkType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="internal">Internal Link</option>
                      <option value="external">External Link</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={formData.linkType === 'external' ? 'https://example.com' : '/packages/123'}
                    />
                  </div>
                </div>

                {/* Package Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Related Package (Optional)
                  </label>
                  <select
                    name="packageId"
                    value={formData.packageId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select a package...</option>
                    {packages.map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Deal Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      name="sortOrder"
                      value={formData.sortOrder}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valid From
                    </label>
                    <input
                      type="date"
                      name="validFrom"
                      value={formData.validFrom}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valid To
                    </label>
                    <input
                      type="date"
                      name="validTo"
                      value={formData.validTo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Discount Badge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Badge
                  </label>
                  <input
                    type="text"
                    name="discountBadge"
                    value={formData.discountBadge}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="50% OFF"
                  />
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Active Status</span>
                    <p className="text-xs text-gray-600">Enable this deal for public display</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !formData.title || (!imageFile && !editingDeal)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Saving...' : (editingDeal ? 'Update Deal' : 'Create Deal')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminLastMinuteDeals;
